import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
  Box,
  Chip,
  Autocomplete,
  Divider,
  Switch,
  IconButton,
  Tooltip,
  Stack,
  Collapse,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import api from '../../services/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  EXAM_MODES,
  QUESTIONS_FILTER,
  TESTS,
  TEST_TYPES,
  TEST_TYPE_OPTIONS,
} from '../../config/Config';
import testService from '../../services/testService';
import { getQuestionImageDisplayUrl } from '../common/QuestionImage';
import MathRenderer from '../common/MathRenderer';
import {
  testDialogFieldLabelSx,
  testDialogFieldShellSx,
  testDialogTopicChipSx,
} from './testDialogFieldStyles';
import TagSelector from '../forms/TagSelector';
import { processTagPayload } from '../../utils/tagPayload';

/** MUI Select treats `value=""` poorly; use a sentinel so "All Levels" stays visible. */
const DIFFICULTY_ALL_SENTINEL = '__all_levels__';

// Default filter state
const defaultFilters = {
  subject: '',
  academic_year: '',
  board: '',
  topic: [],
  difficulty: '',
};

// Default form state
const defaultFormState = {
  title: '',
  type: TEST_TYPES.SKILL_CHECK,
  duration: 60,
  subject: '',
  year: '',
  board: '',
  topics: []
};

const ManualTestDialog = (props) => {
  const { open, onClose, onSave, subjects, years, boards, topics: allTopics, difficulties = [], isFromAssignment = false } = props;
  const [examModes, setExamModes] = useState([]);
  const [testMode, setTestMode] = useState('practice');



  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionPageSize, setQuestionPageSize] = useState(10);
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [topicOptions, setTopicOptions] = useState([]);
  /** Topics with question counts based on current filters (board, academic_year, subject, difficulty). */
  const [topicsWithFilteredCounts, setTopicsWithFilteredCounts] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [testSubject, setTestSubject] = useState('');
  const [testYear, setTestYear] = useState('');
  const [testBoard, setTestBoard] = useState('');
  const [testTitle, setTestTitle] = useState('');
  const [testType, setTestType] = useState(TEST_TYPES.SKILL_CHECK);
  const [testDuration, setTestDuration] = useState(60);
  const [testTopics, setTestTopics] = useState([]);
  const [testTags, setTestTags] = useState([]);

  // Filter test type options based on whether dialog is launched from assignment page
  const filteredTestTypeOptions = isFromAssignment 
    ? TEST_TYPE_OPTIONS 
    : TEST_TYPE_OPTIONS.filter(option => option.value !== TEST_TYPES.REVISE);
  const [saving, setSaving] = useState(false);
  const [myQuestions, setMyQuestions] = useState(false);
  const [dialogExpanded, setDialogExpanded] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Reset form when dialog is opened/closed
  useEffect(() => {
    if (open) {
      // Reset all state when dialog is opened
      setQuestions([]);
      setSelectedQuestions([]);
      setFilters({ ...defaultFilters });
      setTopicOptions([]);
      setTopicsWithFilteredCounts([]);
      setTestSubject('');
      setTestYear('');
      setTestTitle('');
      setTestType(TEST_TYPES.SKILL_CHECK);
      setTestDuration(60);
      setTestTopics([]);
      setTestTags([]);
      setSaving(false);
      setMyQuestions(false);
      // load exam modes
      (async ()=>{
        try{ const res = await api.get(EXAM_MODES + '?page_size=100'); setExamModes(res.data.results||res.data||[]);}catch{ setExamModes([]);} })();
    }
  }, [open]);

  // Update topic options when subject changes (for fallback list without filtered counts)
  useEffect(() => {
    if (filters.subject && allTopics) {
      const subjectId = typeof filters.subject === 'object' ? filters.subject.id : filters.subject;
      const filtered = allTopics.filter(topic => topic.subject == subjectId);
      setTopicOptions(filtered);
      if (subjectId && !testSubject) setTestSubject(subjectId);
    } else {
      setTopicOptions([]);
    }
  }, [filters.subject, allTopics, testSubject]);

  // Fetch topics with question counts based on all current filters (board, academic_year, subject, difficulty)
  useEffect(() => {
    if (!filters.board || !filters.academic_year || !filters.subject) {
      setTopicsWithFilteredCounts([]);
      return;
    }
    let cancelled = false;
    setLoadingTopics(true);
    testService.fetchCurriculumSubjectTopics({
      subject: filters.subject,
      board: filters.board,
      academicYear: filters.academic_year,
      difficulty: filters.difficulty,
      mineOnly: myQuestions,
      examMode: testMode && String(testMode).toLowerCase() !== 'practice' ? testMode : 'practice',
    })
      .then((res) => {
        if (cancelled) return;
        setTopicsWithFilteredCounts(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (!cancelled) setTopicsWithFilteredCounts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTopics(false);
      });
    return () => { cancelled = true; };
  }, [filters.board, filters.academic_year, filters.subject, filters.difficulty, myQuestions, testMode]);

  const topicsWithAvailableQuestions = useMemo(
    () => topicsWithFilteredCounts.filter((t) => (t.question_count ?? 0) > 0),
    [topicsWithFilteredCounts]
  );

  /** Drop topic selections that no longer have any matching questions under current filters. */
  useEffect(() => {
    if (loadingTopics) return;
    setFilters((prev) => {
      if (!prev.topic?.length) return prev;
      const allowed = new Set(topicsWithAvailableQuestions.map((t) => String(t.id)));
      const kept = prev.topic.filter((id) => allowed.has(String(id)));
      if (kept.length === prev.topic.length) return prev;
      return { ...prev, topic: kept };
    });
  }, [topicsWithAvailableQuestions, loadingTopics]);

  // Keep testYear aligned with filters.academic_year (including clear)
  useEffect(() => {
    if (!filters.academic_year) {
      setTestYear('');
      return;
    }
    const yearId =
      typeof filters.academic_year === 'object'
        ? filters.academic_year.id
        : filters.academic_year;
    if (yearId == null || yearId === '') {
      setTestYear('');
      return;
    }
    setTestYear(String(yearId));
  }, [filters.academic_year]);

  // Fetch questions based on filters
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Always include subject filter
      params.append('subject', String(filters.subject));
      if (!testSubject) setTestSubject(filters.subject);

      // Add board if selected
      if (filters.board) {
        params.append('board', String(filters.board));
        if (!testBoard) setTestBoard(String(filters.board));
      }
      // Add academic year if selected
      if (filters.academic_year) {
        const yearId = String(filters.academic_year);
        params.append('academic_year', yearId);
        if (!testYear) setTestYear(yearId);
      }

      // Add topic filter if selected
      if (filters.topic && filters.topic.length > 0) {
        // Add each selected topic ID to the query params
        filters.topic.forEach(topicId => {
          if (topicId && topicId !== 'undefined') {
            params.append('topic', topicId);
          }
        });

        // Keep testTopics in sync with current filter selection
        if (allTopics) {
          const selectedTopics = allTopics.filter(t =>
            t && filters.topic.includes(String(t.id))
          );
          setTestTopics(selectedTopics);
        }
      }

      // Add difficulty filter if selected
      if (filters.difficulty) {
        params.append('difficulty', filters.difficulty);
      }

      // exam_mode aligns with topic counts (SubjectTopicViewSet) and test mode picker

      // Add active filter
      params.append('is_active', '1');

      // Add flag for creator filter
      if (myQuestions) {
        params.append('mine', '1');
      }

      if (testMode && String(testMode).toLowerCase() !== 'practice') {
        params.append('exam_mode', String(testMode));
      } else {
        params.append('exam_mode', 'practice');
      }

      const queryString = params.toString();
      const apiUrl = queryString ? `${QUESTIONS_FILTER}?${queryString}` : QUESTIONS_FILTER;

      const response = await api.get(apiUrl);

      let questionsData = [];
      const body = response.data;
      if (body && Array.isArray(body.results)) {
        questionsData = body.results;
      } else if (Array.isArray(body)) {
        questionsData = body;
      }
      

      
      setQuestions(questionsData);
    } catch (error) {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [
    filters.subject,
    filters.academic_year,
    filters.topic,
    filters.difficulty,
    filters.board,
    testSubject,
    testYear,
    testMode,
    JSON.stringify(testTopics.map((t) => t.id)),
    myQuestions,
  ]);

  // Fetch questions when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.subject && filters.board && filters.academic_year && Array.isArray(filters.topic) && filters.topic.length>0) {
        fetchQuestions();
      } else {
        setQuestions([]);
      }
    }, 300); // Reduced debounce time for better responsiveness
    
    return () => clearTimeout(timer);
  }, [filters, fetchQuestions, testMode]);

  const handleMyQuestionsChange = (event) => {
    setMyQuestions(event.target.checked);
    setQuestions([]);
    setSelectedQuestions([]);
    setTopicsWithFilteredCounts([]);
    setTestTopics([]);
    setFilters((prev) => ({ ...prev, topic: [] }));
  };

  const handleFilterChange = (field) => (event, value) => {
    
    // Handle different field types
    if (field === 'difficulty') {
      // Handle difficulty filter (from native select)
      const newValue = event?.target?.value || '';

      setFilters(prev => ({
        ...prev,
        difficulty: newValue
      }));
      return;
    } 
    
    // For subject and academic_year, extract the ID if it's an object
    const newValue = value && value.id != null ? value.id : (event?.target?.value || '');
    
    if (field === 'subject' && newValue) {
      // When subject changes, update subject and clear topics
      setTestSubject(newValue);
      setTestTopics([]);
      setFilters(prev => ({
        ...prev,
        subject: String(newValue),
        topic: [] // Clear topic when subject changes
      }));
    } else if (field === 'subject' && !newValue) {
      setTestSubject('');
      setTestTopics([]);
      setFilters((prev) => ({ ...prev, subject: '', topic: [] }));
    } else if (field === 'board' && newValue) {
      setTestBoard(String(newValue));
      setFilters(prev=>({...prev, board:String(newValue)}));
    } else if (field === 'board' && !newValue) {
      setTestBoard('');
      setFilters((prev) => ({ ...prev, board: '' }));
    } else if (field === 'academic_year') {
      const id =
        value && typeof value === 'object' && value.id != null
          ? value.id
          : newValue !== undefined && newValue !== null && newValue !== ''
            ? newValue
            : '';
      const str = id === '' || id == null ? '' : String(id);
      setTestYear(str);
      setFilters((prev) => ({ ...prev, academic_year: str }));
    } else if (field === 'topic') {
      // Topic is handled directly in the Autocomplete's onChange
      return;
    } else {
      // For any other fields, just update the value
      setFilters(prev => ({
        ...prev,
        [field]: String(newValue)
      }));
    }
    

  };

  const handleQuestionSelect = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSave = async () => {
    if (saving) {
      return; // prevent duplicate submissions
    }
    if (!testTitle.trim()) {
      alert('Please enter a test title');
      return;
    }

    if (!testSubject) {
      alert('Please select a subject');
      return;
    }
    if (!testYear) {
      alert('Please select an academic year');
      return;
    }
    
    if (!testTopics || testTopics.length === 0) {
      alert('Please select at least one topic. You can select multiple');
      return;
    }

    if (selectedQuestions.length === 0) {
      alert('Please select at least one question');
      return;
    }

    setSaving(true);
    try {

      // First create the test
      const tagIds = await processTagPayload(testTags);
      const testData = {
        title: testTitle,
        subject: testSubject,
        academic_year: testYear || null,
        test_type: testType,
        num_questions: selectedQuestions.length,
        duration_minutes: testDuration,
        percent_basic: 0, // Will be calculated on the backend
        percent_advanced: 0,
        created_for_board: testBoard || null,
        note: 'Created via manual question selection',
        topics: testTopics.map(t => t.id),
        tags: tagIds
      };

      // Create the test
      const testRes = await api.post(TESTS, { ...testData, exam_mode: (testMode === 'practice' ? null : testMode) });

      
      if (!testRes.data || !testRes.data.id) {
        throw new Error('Invalid response when creating test: Missing test ID');
      }
      
      const testId = testRes.data.id;

      // Prepare question IDs - ensure they are numbers
      const questionIds = selectedQuestions.map(id => Number(id));


      // Add questions to the test (detail action: POST /api/exams/tests/{id}/add_questions/)
      const addRes = await api.post(`${TESTS}${testId}/add_questions/`, {
        question_ids: questionIds
      });
      


      if (!addRes.data || typeof addRes.data.added_count === 'undefined') {

      }

      // Notify parent with the created test data and selected question IDs
      onSave(selectedQuestions, {
        ...testRes.data,
        title: testTitle,
        subject: testSubject,
        academic_year: testYear || null,
        test_type: testType,
        duration_minutes: testDuration,
        created_for_board: testBoard || null,
        topics: testTopics.map(t => t.id),
        tags: tagIds,
        added_questions: addRes.data?.added_count || questionIds.length,
        total_questions: addRes.data?.total_questions || questionIds.length
      });
      
      // Reset form
      setSelectedQuestions([]);
      setTestTitle('');
      setTestType(TEST_TYPES.SKILL_CHECK);
      setTestDuration(60);
      setTestTopics([]);
      setTestTags([]);
      onClose();
    } catch (error) {
      
      let errorMessage = 'Failed to create test. ';
      if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: 'select',
      headerName: 'Select',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => null, // Remove header
      renderCell: (params) => {
        return (
          <Checkbox
            checked={selectedQuestions.includes(params.row.id)}
            onChange={(e) => {
              e.stopPropagation();
              handleQuestionSelect(params.row.id);
            }}
            color="primary"
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'question_text', 
      headerName: 'Question', 
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => {
        // Enhanced render: show text, image, options with correct shaded, and sub-questions
        const row = params.row || {};
        const textRaw = row.question_text || '';
        const optionsArr = Array.isArray(row.options) ? row.options : (()=>{try{return JSON.parse(row.options);}catch{return [];}})();
        // Same image rendering as teacher View Question
        const imgUrl = (row.image_proxy_url || row.image_url) ? getQuestionImageDisplayUrl(row) : '';
        const correct = row.correct_answer;
        const hasSubQuestions = row.has_subquestions && Array.isArray(row.sub_questions) && row.sub_questions.length > 0;

        return (
          <Box sx={{ p:1 }}>
              <Box sx={{ mb: 1, fontWeight: hasSubQuestions ? 600 : 'normal' }}>
                <MathRenderer content={textRaw} variant="body2" sx={{ whiteSpace: 'normal' }} />
                {hasSubQuestions && (
                  <Chip 
                    label={`${row.sub_questions.length} sub-question${row.sub_questions.length > 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                  />
                )}
              </Box>
              {imgUrl && (
                <Box sx={{ mb:1 }}>
                  <img src={imgUrl} alt="question" style={{ maxWidth:'160px', maxHeight:'120px', objectFit: 'contain', borderRadius: 8, border: '1px solid #e0e0e0' }} />
                </Box>
              )}
              {optionsArr && optionsArr.length>0 && !hasSubQuestions && (
                <Box component="ol" sx={{ pl:2, m:0 }}>
                  {optionsArr.map((opt, idx)=>(
                    <li key={idx} style={{ marginBottom:2 }}>
                      <Box sx={{
                        backgroundColor: String(opt)===String(correct)? 'rgba(76,175,80,0.25)': 'transparent',
                        px:1,
                        borderRadius:1,
                        display:'inline-flex',
                        alignItems:'center'
                      }}>
                        <MathRenderer content={String(opt)} variant="body2" />
                        {String(opt)===String(correct) && <CheckCircleOutlineIcon sx={{ fontSize:14, ml:0.5, color:'success.main' }} />}
                      </Box>
                    </li>
                  ))}
                </Box>
              )}
              {/* Display sub-questions */}
              {hasSubQuestions && (
                <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
                    Sub-questions:
                  </Typography>
                  <Box sx={{ pl: 1 }}>
                    {/* Sort sub-questions by order field if available */}
                    {[...row.sub_questions].sort((a, b) => {
                      const orderA = a.order ?? a.order_number ?? 0;
                      const orderB = b.order ?? b.order_number ?? 0;
                      if (orderA !== orderB) return orderA - orderB;
                      return (a.id ?? 0) - (b.id ?? 0);
                    }).map((subQ) => {
                      const subOptionsArr = Array.isArray(subQ.options) ? subQ.options : (()=>{try{return JSON.parse(subQ.options);}catch{return [];}})();
                      const subCorrect = subQ.correct_answer;
                      return (
                        <Box key={subQ.id} sx={{ mb: 1.5 }}>
                          <MathRenderer
                            content={(subQ.letter ? `${subQ.letter}. ` : '') + ((subQ.question_text || subQ.text || '').trim() || '—')}
                            variant="body2"
                            sx={{ wordBreak: 'break-word', fontWeight: 500 }}
                          />
                          {subOptionsArr && subOptionsArr.length > 0 && (
                            <Box component="ol" sx={{ pl: 2, m: 0.5, mt: 0.5 }}>
                              {subOptionsArr.map((opt, optIdx) => {
                                const isCorrect = String(opt) === String(subCorrect);
                                return (
                                  <li key={optIdx} style={{ marginBottom: 2 }}>
                                    <Box sx={{
                                      backgroundColor: isCorrect ? 'rgba(76,175,80,0.25)' : 'transparent',
                                      px: 1,
                                      borderRadius: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center'
                                    }}>
                                      <MathRenderer content={String(opt)} variant="body2" />
                                      {isCorrect && <CheckCircleOutlineIcon sx={{ fontSize: 14, ml: 0.5, color: 'success.main' }} />}
                                    </Box>
                                  </li>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
        );
      }
    },
    { 
      field: 'topics', 
      headerName: 'Topics', 
      flex: 1,
      renderCell: (params) => {
        const topics = params.row.topics || [];
        if (!topics.length) return null;
        
        return (
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            maxHeight: '80px',
            overflow: 'hidden'
          }}>
            {topics.map((topic, index) => (
              <Chip 
                key={index}
                label={topic.topic || topic.topic_name || topic.name || 'Untitled'}
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem',
                  maxWidth: '150px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
            ))}
          </div>
        );
      }
    },
    { 
      field: 'difficulty', 
      headerName: 'Difficulty', 
      width: 120,
      renderCell: (params) => {
        const difficulty = params.value || 'Basic';
        return (
          <Chip 
            label={difficulty} 
            size="small"
            color={
              difficulty === 'Advanced' ? 'error' : 
              difficulty === 'Intermediate' ? 'warning' : 'success'
            }
            sx={{ minWidth: '80px' }}
          />
        );
      }
    },
  ];

  const handleDialogClose = (event, reason) => {
    if (saving || reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      fullScreen={dialogExpanded}
      maxWidth={dialogExpanded ? false : 'xl'}
      PaperProps={{
        sx: dialogExpanded
          ? {
              display: 'flex',
              flexDirection: 'column',
              m: 0,
              maxHeight: '100%',
              height: '100%',
              borderRadius: 0,
              overflow: 'hidden',
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              width: 'min(1320px, 96vw)',
              maxWidth: 'min(1320px, 96vw) !important',
              height: 'min(92vh, 920px)',
              maxHeight: 'min(92vh, 920px)',
              overflow: 'hidden',
            },
      }}
    >
      <DialogTitle sx={{ pr: 1, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
            Create Test - Manual Selection
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {saving && (
              <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Saving...
                </Typography>
              </Box>
            )}
            <Tooltip title={dialogExpanded ? 'Exit fullscreen' : 'Expand dialog'}>
              <span>
                <IconButton
                  edge="end"
                  onClick={() => setDialogExpanded((e) => !e)}
                  disabled={saving}
                  aria-label={dialogExpanded ? 'Exit fullscreen' : 'Expand dialog'}
                  size="small"
                >
                  {dialogExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
          p: { xs: 2, sm: 2.75 },
          bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'background.default'),
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: filtersCollapsed ? 0.5 : 1 }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              endIcon={filtersCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              onClick={() => setFiltersCollapsed((c) => !c)}
              aria-expanded={!filtersCollapsed}
              id="manual-test-toggle-filters"
            >
              {filtersCollapsed ? 'Show test filters' : 'Hide test filters'}
            </Button>
          </Box>
          <Collapse in={!filtersCollapsed} timeout="auto" collapsedSize={0}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Grid container spacing={2.25}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 0.75, sm: 2 },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={myQuestions}
                          onChange={handleMyQuestionsChange}
                          color="primary"
                          disabled={saving}
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={600}>
                          Use my questions only
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 760 }}>
                      On: Your questions only. Off: Full curriculum bank.
                    </Typography>
                  </Box>
                </Grid>

                {/* Row 1: Title, duration, test type, test mode */}
                <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="manual-test-title" sx={testDialogFieldLabelSx}>
                  Test title *
                </Typography>
                <TextField
                  id="manual-test-title"
                  fullWidth
                  hiddenLabel
                  size="small"
                  placeholder="e.g. End of Term Mathematics Assessment"
                  value={testTitle}
                  onChange={(e) => setTestTitle(e.target.value)}
                  required
                  disabled={saving}
                  sx={testDialogFieldShellSx}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="manual-test-duration" sx={testDialogFieldLabelSx}>
                  Duration (min)
                </Typography>
                <TextField
                  id="manual-test-duration"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={testDuration}
                  onChange={(e) => setTestDuration(e.target.value)}
                  disabled={saving}
                  sx={testDialogFieldShellSx}
                >
                  {[15, 30, 45, 60, 90, 120].map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes} min
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="manual-test-type" sx={testDialogFieldLabelSx}>
                  Test type *
                </Typography>
                <TextField
                  id="manual-test-type"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  value={testType}
                  onChange={(e) => setTestType(e.target.value)}
                  required
                  disabled={saving}
                  sx={testDialogFieldShellSx}
                >
                  {filteredTestTypeOptions.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography component="label" htmlFor="manual-test-mode" sx={testDialogFieldLabelSx}>
                  Test mode *
                </Typography>
                <TextField
                  id="manual-test-mode"
                  select
                  fullWidth
                  hiddenLabel
                  size="small"
                  required
                  value={testMode}
                  onChange={(e) => setTestMode(e.target.value)}
                  disabled={saving}
                  sx={testDialogFieldShellSx}
                >
                  <MenuItem value="practice">Practice</MenuItem>
                  {examModes
                    .filter((m) => String(m.mode).toLowerCase() !== 'practice')
                    .map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.mode}
                      </MenuItem>
                    ))}
                </TextField>
              </Stack>
            </Grid>
                {/* Row 2: Board, academic year, subject, topics */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Board</Typography>
                <Autocomplete
                  options={boards || []}
                  value={boards?.find((b) => String(b.id) === String(filters.board)) || null}
                  onChange={(_, value) => handleFilterChange('board')(_, value)}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return option.name || option.board_name || String(option.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return String(option.id) === String(value.id || value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      hiddenLabel
                      placeholder="Select board"
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={testDialogFieldShellSx}
                    />
                  )}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Academic year</Typography>
                <Autocomplete
                  options={years || []}
                  value={years?.find((y) => String(y.id) === String(filters.academic_year)) || null}
                  onChange={(_, value) => handleFilterChange('academic_year')(_, value)}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    if (option.year != null && option.year !== '') return `Grade ${option.year}`;
                    if (option.label) return option.label;
                    return String(option.id ?? '');
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return String(option.id) === String(value.id || value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      hiddenLabel
                      placeholder="Select year"
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={testDialogFieldShellSx}
                    />
                  )}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Subject</Typography>
                <Autocomplete
                  options={subjects || []}
                  value={subjects?.find((s) => String(s.id) === String(filters.subject)) || null}
                  onChange={(_, value) => handleFilterChange('subject')(_, value)}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return option.name || option.subject_name || String(option.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return String(option.id) === String(value.id || value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, id: 'manual-test-subject-input' }}
                      hiddenLabel
                      placeholder="Select subject"
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={testDialogFieldShellSx}
                    />
                  )}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.75}>
                <Typography sx={testDialogFieldLabelSx}>Topics</Typography>
                <Autocomplete
                  multiple
                  disabled={
                    !(filters.board && filters.academic_year && filters.subject) || loadingTopics
                  }
                  options={
                    filters.board && filters.academic_year && filters.subject
                      ? topicsWithAvailableQuestions
                      : []
                  }
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    const topicName = option.topic || `Topic ${option.id}`;
                    const questionCount = option.question_count ?? 0;
                    return `${topicName} (${questionCount} Qs)`;
                  }}
                  value={topicsWithAvailableQuestions.filter(
                    (topic) => filters.topic && filters.topic.includes(String(topic.id))
                  )}
                  onChange={(_, newValue) => {
                    setFilters((prev) => ({
                      ...prev,
                      topic: newValue ? newValue.map((topic) => String(topic.id)) : [],
                    }));
                  }}
                  ListboxProps={{
                    'data-testid': 'manual-test-topic-listbox',
                    style: { maxHeight: 320, overflowY: 'auto' },
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const topicName = option.topic || `Topic ${option.id}`;
                      const questionCount = option.question_count ?? 0;
                      const label = `${topicName} (${questionCount} Qs)`;
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={label}
                          size="small"
                          sx={testDialogTopicChipSx}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      hiddenLabel
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Type to add topics…"
                      sx={testDialogFieldShellSx}
                      helperText={
                        !(filters.board && filters.academic_year && filters.subject)
                          ? 'Select board, year, and subject first.'
                          : loadingTopics
                            ? 'Loading topics…'
                            : topicsWithAvailableQuestions.length === 0
                              ? 'No topics have questions for the current filters. Adjust board, year, subject, difficulty, or turn off "Use my questions only".'
                              : undefined
                      }
                      FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const topicName = option.topic || `Topic ${option.id}`;
                    const questionCount = option.question_count ?? 0;
                    const displayText = `${topicName} (${questionCount} Qs)`;
                    return (
                      <li {...props} key={option.id}>
                        {displayText}
                      </li>
                    );
                  }}
                  size="small"
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    const optionId = option.id || option.value;
                    const valueId = value.id || value.value;
                    return String(optionId) === String(valueId);
                  }}
                />
              </Stack>
            </Grid>
                {/* Row 3: Difficulty */}
                <Grid item xs={12}>
                  <Stack spacing={0.75}>
                    <Typography component="label" htmlFor="manual-test-difficulty" sx={testDialogFieldLabelSx}>
                      Difficulty
                    </Typography>
                    <TextField
                      id="manual-test-difficulty"
                      select
                      fullWidth
                      hiddenLabel
                      size="small"
                      value={
                        filters.difficulty === '' || filters.difficulty == null
                          ? DIFFICULTY_ALL_SENTINEL
                          : String(filters.difficulty)
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((prev) => ({
                          ...prev,
                          difficulty: v === DIFFICULTY_ALL_SENTINEL ? '' : v,
                        }));
                      }}
                      variant="outlined"
                      sx={testDialogFieldShellSx}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected || selected === DIFFICULTY_ALL_SENTINEL) {
                            return 'All levels';
                          }
                          const d = (difficulties || []).find((x) => String(x.id) === String(selected));
                          return d?.name || d?.label || `Difficulty ${selected}`;
                        },
                      }}
                    >
                      <MenuItem value={DIFFICULTY_ALL_SENTINEL}>All levels</MenuItem>
                      {Array.isArray(difficulties) &&
                        difficulties.map((d) => (
                          <MenuItem key={d.id} value={String(d.id)}>
                            {d.name || d.label || `Difficulty ${d.id}`}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={0.75}>
                    <Typography sx={testDialogFieldLabelSx}>Tags</Typography>
                    <TagSelector
                      value={testTags}
                      onChange={setTestTags}
                      label=""
                      helperText="Add descriptive tags (optional)"
                      disabled={saving}
                      size="small"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            mt: 1,
          }}
        >
        <Divider sx={{ my: 2, flexShrink: 0 }} />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            columnGap: 2,
            mb: 1,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: '0.08em',
              fontWeight: 700,
              color: 'primary.dark',
            }}
          >
            Select questions
          </Typography>
          {selectedQuestions.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {selectedQuestions.length}{' '}
              {selectedQuestions.length === 1 ? 'question' : 'questions'} selected
            </Typography>
          )}
        </Box>
        <Paper
          variant="outlined"
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >

        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: { xs: 200, sm: 260 },
            width: '100%',
            position: 'relative',
            '& .MuiDataGrid-root': {
              position: 'absolute',
              inset: 0,
              width: '100% !important',
              height: '100% !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {questions.length > 0 ? (
            <DataGrid
              rows={questions}
              columns={columns}
              pageSize={questionPageSize}
              onPageSizeChange={(newPageSize) => setQuestionPageSize(newPageSize)}
              rowsPerPageOptions={[10, 25, 50]}
              getRowHeight={() => 'auto'}
              disableSelectionOnClick
              loading={loading}
              components={{
                Toolbar: () => null,
                BaseCheckbox: () => null, 
                NoRowsOverlay: () => (
                  <Box sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                      No questions found
                    </Typography>
                  </Box>
                ),
              }}
              getRowId={(row) => row.id}
              onRowClick={(params) => handleQuestionSelect(params.row.id)}
              sx={{
                height: '100%',
                border: 'none',
                '& .MuiDataGrid-cell': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiDataGrid-columnHeaderCheckbox': {
                  display: 'none',
                },
              }}
              hideFooterSelectedRowCount
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                textAlign: 'center',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {loading ? 'Loading questions...' : 'No questions found'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {loading 
                  ? 'Please wait while we load the questions...'
                  : filters.subject 
                    ? 'No questions match your current filters.'
                    : 'Please select a subject to view available questions.'}
              </Typography>
              {!loading && !filters.subject && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small" 
                  sx={{ mt: 2 }}
                  onClick={() => document.getElementById('manual-test-subject-input')?.focus()}
                >
                  Select Subject
                </Button>
              )}
            </Box>
          )}
        </Box>
        </Paper>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          p: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSave}
          disabled={
            saving ||
            !(testTitle && testTitle.trim()) ||
            !testSubject ||
            !testYear ||
            !(testTopics && testTopics.length > 0) ||
            selectedQuestions.length === 0
          }
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {saving ? 'Creating Test...' : `Create Test ${selectedQuestions.length > 0 ? `(${selectedQuestions.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualTestDialog;
